import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  addSnmpChecks,
  snmpCheckFields,
  snmpChecksFormQuery,
  snmpServiceDevicesQuery,
  snmpServicesTableQuery,
  type SnmpCheckField,
  type SnmpServiceDeviceRow,
} from '../api/proactivitee'
import { escapeHtml } from '../inventoree/format'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { servicesColumns } from './snmpColumns'

/**
 * SNMP Services - `#snmpAlarms` plus Add checks (in bara de sus), aceleasi
 * coloane ca SnmpServicesModel. Expand: tab-uri Critical / Warning / Up.
 */

export function SnmpServicesPage() {
  const [state, setState] = useTableState('snmpAlarms', { start: 0, length: 25, order: { column: 1, dir: 'asc' }, search: '' })
  const [addOpen, setAddOpen] = useState(false)
  const columns = useMemo(() => servicesColumns(), [])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[1]
  const query = useQuery({
    ...snmpServicesTableQuery({
      draw: 1, start: state.start, length: state.length,
      orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const canAdd = Boolean(query.data?.canAdd)

  return (
    <Content>
      <div id="content">
        <DataTable
          id="snmpAlarms"
          columnTools
          columns={columns}
          rows={query.data?.data ?? []}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          details={(row) => <ServiceTabs serviceId={Number(row.ID)} />}
          /* Add checks in bara de sus, langa cautare - vezi antetul din
             `SnmpHostsPage.tsx` pentru de ce banda de deasupra tabelului nu e
             locul lui nici in portal. */
          headerButtons={
            canAdd ? (
              <button
                type="button"
                className="btn btn-primary em-add-open tk-header-extra"
                onClick={() => setAddOpen(true)}
              >
                <i className="fa fa-plus" /> Add checks
              </button>
            ) : undefined
          }
          /* `buttons: DataTablesProperties.buttons` in SnmpServicesDatatable. */
          footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
        >
          {canAdd && addOpen ? (
            <AddChecksModal onClose={() => setAddOpen(false)} onAdded={() => void query.refetch()} />
          ) : null}
        </DataTable>
      </div>
    </Content>
  )
}

/** Culoarea si latimea coloanei de descriere tin de fila, ca in serviceDevices.php. */
const STATUS_LOOK: Record<number, { label: string; width: string }> = {
  0: { label: 'label-primary', width: '25%' },
  1: { label: 'label-danger', width: '50%' },
  2: { label: 'label-warning', width: '50%' },
}

const TABS: { status: number; title: string }[] = [
  { status: 1, title: 'Critical' },
  { status: 2, title: 'Warning' },
  { status: 0, title: 'Up' },
]

/**
 * Randul desfacut al unui serviciu - services/tabList.php si serviceDevices.php.
 * Se desena numai numele masinii; lipseau Service details, Description, Outage
 * date, Recover (numai pe Up) si durata caderii - adica tot ce spune ce anume
 * s-a intamplat. Numele masinii se scrie o singura data pe grup (rowspan).
 */
function ServiceTabs({ serviceId }: { serviceId: number }) {
  const [status, setStatus] = useState(1)
  const query = useQuery(snmpServiceDevicesQuery(serviceId, status))
  const rows: SnmpServiceDeviceRow[] = query.data?.data ?? []
  const look = STATUS_LOOK[status] ?? STATUS_LOOK[1]
  const up = status === 0

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {TABS.map((t) => (
            <li key={t.status}>
              <a
                href={`#${t.title.toLowerCase()}`}
                className={`action_button${t.status === status ? ' color-red' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  setStatus(t.status)
                }}
              >
                {t.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {query.isPending ? (
            <div><i className="fas fa-circle-notch fa-spin"></i> Loading...</div>
          ) : rows.length === 0 ? (
            <div className="col-md-12">
              <div className="alert alert-info"><i className="fas fa-exclamation-circle"></i> No devices.</div>
            </div>
          ) : (
            <table className="table">
              <tbody>
                <tr>
                  <th>Device name</th>
                  <th>Service details</th>
                  <th style={{ width: look.width }}>Description</th>
                  <th>Outage date</th>
                  {up ? <th style={{ width: look.width }}>Recover details</th> : null}
                  {up ? <th>Recover date</th> : null}
                  <th>Outage duration</th>
                </tr>
                {rows.map((row) =>
                  row.entries.map((entry, i) => (
                    <tr key={`${row.device_id}:${i}`}>
                      {i === 0 ? (
                        <td rowSpan={row.entries.length} style={{ verticalAlign: 'top' }}>
                          {row.device_name}
                        </td>
                      ) : null}
                      <td><span className={`label ${look.label}`}>{entry.service_details}</span></td>
                      <td style={{ width: look.width }}>{entry.description}</td>
                      <td>{entry.outage_date}</td>
                      {up ? <td style={{ width: look.width }}>{entry.recover_details}</td> : null}
                      {up ? <td>{entry.recover_date}</td> : null}
                      <td>{entry.duration}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

/** Fereastra de adaugare; se monteaza doar cat sta deschisa. */
/**
 * Adaugarea unor verificari pe una sau mai multe gazde (addChecks.php).
 *
 * O verificare nu e un bec pornit sau stins: are port, prag de avertisment,
 * prag critic. Cine adauga "check_http" pe zece masini fara sa le vada adauga
 * zece verificari despre care nu stie ce masoara. De-aia, la fiecare schimbare
 * a selectiei, se cer campurile verificarilor bifate si se deseneaza GOALE, cu
 * implicita din sablon ca sugestie: gol = fiecare masina isi ia valoarea ei
 * (sablonul e pe tip de masina); scris = merge la toate.
 */
function AddChecksModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const form = useQuery(snmpChecksFormQuery())
  const [error, setError] = useState('')
  const [picked, setPicked] = useState<number[]>([])
  const [fields, setFields] = useState<Record<string, SnmpCheckField[]>>({})
  const [values, setValues] = useState<Record<string, Record<string, string>>>({})
  const hosts = form.data?.hosts ?? []
  const checks = form.data?.checks ?? []

  useEffect(() => {
    if (picked.length === 0) return
    let live = true
    snmpCheckFields(picked)
      .then((res) => {
        if (live) setFields(res.fields ?? {})
      })
      .catch(() => {
        if (live) setFields({})
      })
    return () => {
      live = false
    }
  }, [picked])

  function readPicked() {
    setPicked(Array.from(document.querySelectorAll<HTMLOptionElement>('#ac-checks option:checked')).map((o) => Number(o.value)))
  }

  function selectAll(target: string, on: boolean) {
    for (const o of document.querySelectorAll<HTMLOptionElement>(`${target} option`)) o.selected = on
    if (target === '#ac-checks') readPicked()
  }

  async function save() {
    const deviceIds = Array.from(document.querySelectorAll<HTMLOptionElement>('#ac-hosts option:checked')).map((o) => Number(o.value))
    const checkIds = Array.from(document.querySelectorAll<HTMLOptionElement>('#ac-checks option:checked')).map((o) => Number(o.value))
    const result = await addSnmpChecks(deviceIds, checkIds, values)
    if (result.status === 'OK') {
      onClose()
      onAdded()
      return
    }
    setError(result.message)
  }

  return (
    <div className="modal fade in" id="emAdd" tabIndex={-1} role="dialog" style={{ display: 'block' }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}><span aria-hidden="true">&times;</span></button>
            <h4 className="modal-title" id="emAddTitle">Add checks to hosts</h4>
          </div>
          <div className="modal-body">
            {error ? <div className="alert alert-danger" id="emAddError">{error}</div> : <div className="alert alert-danger" id="emAddError" style={{ display: 'none' }} />}
            <div id="emAddBody">
              <form id="add-checks-form">
                {hosts.length === 0 ? (
                  <div className="alert alert-info" role="alert">
                    <p style={{ marginBottom: 0 }}>
                      No device is watched from outside yet. Add a host first, from
                      {' '}<strong>SNMP Hosts</strong> - a check needs somewhere to run against.
                    </p>
                  </div>
                ) : checks.length === 0 ? (
                  <div className="alert alert-info" role="alert"><p style={{ marginBottom: 0 }}>There are no check types defined.</p></div>
                ) : (
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="ac-hosts">Hosts <small className="text-muted">({hosts.length} watched)</small></label>
                        <select name="devices[]" id="ac-hosts" className="form-control" multiple size={10} required>
                          {hosts.map((h) => (
                            <option key={String(h.ID)} value={String(h.ID)}>
                              {String(h.Denumire)}{h.ip ? ` · ${String(h.ip)}` : ''}
                            </option>
                          ))}
                        </select>
                        <span className="help-block">
                          <a href="#" className="ac-all" data-target="#ac-hosts" onClick={(e) => { e.preventDefault(); selectAll('#ac-hosts', true) }}>Select all</a> &middot;{' '}
                          <a href="#" className="ac-none" data-target="#ac-hosts" onClick={(e) => { e.preventDefault(); selectAll('#ac-hosts', false) }}>Clear</a>
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="ac-checks">What to check</label>
                        <select name="checks[]" id="ac-checks" className="form-control" multiple size={10} required onChange={readPicked}>
                          {checks.map((c) => (
                            <option key={String(c.ID)} value={String(c.ID)}>{String(c.name)}</option>
                          ))}
                        </select>
                        <span className="help-block">
                          <a href="#" className="ac-all" data-target="#ac-checks" onClick={(e) => { e.preventDefault(); selectAll('#ac-checks', true) }}>Select all</a> &middot;{' '}
                          <a href="#" className="ac-none" data-target="#ac-checks" onClick={(e) => { e.preventDefault(); selectAll('#ac-checks', false) }}>Clear</a>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div id="ac-fields">
                  {picked.map((id) => {
                    const list = fields[String(id)]
                    if (!list || list.length === 0) return null
                    const name = checks.find((c) => Number(c.ID) === id)?.name
                    return (
                      <div key={id} className="ac-check">
                        <strong>{String(name ?? id)}</strong>
                        <div className="row">
                          {list.map((f) => (
                            <div key={f.field} className="col-md-4">
                              <div className="input-group">
                                <span className="input-group-addon">{f.field}</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name={`values[${id}][${f.field}]`}
                                  placeholder={(f.suggestion === '' ? 'default' : f.suggestion) + (f.varies ? ' - varies by machine type' : '')}
                                  value={values[String(id)]?.[f.field] ?? ''}
                                  onChange={(e) =>
                                    setValues((cur) => ({ ...cur, [String(id)]: { ...(cur[String(id)] ?? {}), [f.field]: e.target.value } }))
                                  }
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {hosts.length > 0 && checks.length > 0 ? (
                  <p className="text-muted" style={{ fontSize: 13 }}>
                    <i className="fa fa-info-circle"></i>{' '}
                    Leave a setting empty and each machine keeps its own default.
                    Checks already there are left alone - nothing is overwritten.
                  </p>
                ) : null}
              </form>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" id="emAddSave" onClick={() => void save()}>Add</button>
          </div>
        </div>
      </div>
      <style>{`
    .ac-check { margin: 0 0 12px; }
    .ac-check > strong { display: block; margin-bottom: 6px; font-size: 13px; }
    .ac-check .input-group { margin-bottom: 6px; }
    .ac-check .input-group-addon { font-size: 12px; }
`}</style>
    </div>
  )
}

export { escapeHtml }
